import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { groupService } from '../../services/groupService';
import { formatCurrency } from '../../utils/helpers';

const CYCLES = [['weekly', 'Weekly'], ['monthly', 'Monthly']];

function StepIndicator({ step }) {
  const steps = ['Contribution Details', 'Invite Members', 'Review'];
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginBottom: '1.75rem', maxWidth: 640 }}>
      {steps.map((label, idx) => {
        const num = idx + 1;
        const active = step === num;
        const done = step > num;
        return (
          <div key={num} style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
              <div style={{
                width: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: done ? '#00C878' : active ? '#1596E0' : 'rgba(255,255,255,0.08)',
                border: `2px solid ${done ? '#00C878' : active ? '#1596E0' : 'rgba(255,255,255,0.15)'}`,
                fontSize: '0.8rem', fontWeight: 700,
                color: (done || active) ? '#fff' : 'var(--text-3)',
                transition: 'all 0.2s',
              }}>
                {done ? '✓' : num}
              </div>
              <span style={{ fontSize: '0.7rem', color: active ? 'var(--text-1)' : 'var(--text-3)', fontWeight: active ? 600 : 400, whiteSpace: 'nowrap' }}>
                {label}
              </span>
            </div>
            {idx < steps.length - 1 && (
              <div style={{ flex: 1, height: 2, background: done ? '#00C878' : 'rgba(255,255,255,0.1)', margin: '0 4px', marginBottom: 20, transition: 'background 0.3s' }} />
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function NewGroup() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);

  const [form, setForm] = useState({ name: '', contribution_amount: '', cycle: 'monthly', max_members: '' });
  const [invites, setInvites] = useState(['']);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = e => setForm({ ...form, [e.target.name]: e.target.value });

  const fetchPreview = useCallback(async (f) => {
    setPreviewLoading(true);
    setPreviewError('');
    try {
      const res = await groupService.previewSchedule({
        contribution_amount: f.contribution_amount, cycle: f.cycle, max_members: f.max_members,
      });
      setPreview(res.data.data);
    } catch (err) {
      setPreviewError(err.response?.data?.message || 'Could not compute preview.');
      setPreview(null);
    } finally {
      setPreviewLoading(false);
    }
  }, []);

  const goToStep2 = () => {
    if (!form.name || !form.contribution_amount || !form.max_members) return;
    setStep(2);
  };

  const goToStep3 = () => {
    fetchPreview(form);
    setStep(3);
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await groupService.createGroup(form);
      const group = res.data.data;
      const emails = invites.map(e => e.trim()).filter(Boolean);
      for (const email of emails) {
        try { await groupService.inviteMember(group.id, { email }); } catch { /* best-effort */ }
      }
      navigate(`/groups/${group.id}`, { state: { success: 'Group created!' } });
    } catch (err) {
      setError(err.response?.data?.message || 'Could not create group.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: 640 }}>
      <div className="page-header">
        <h1>New Group</h1>
        <p>Set up a rotating contribution circle (Ajo)</p>
      </div>

      <StepIndicator step={step} />

      {step === 1 && (
        <div className="card">
          <div className="form-group">
            <label className="form-label">Group Name</label>
            <input className="form-input" name="name" value={form.name} onChange={handleChange} placeholder="e.g. Office Ajo 2026" />
          </div>
          <div className="form-group">
            <label className="form-label">Contribution Amount Per Member Per Cycle (₦)</label>
            <input className="form-input" type="number" name="contribution_amount" value={form.contribution_amount} onChange={handleChange} placeholder="e.g. 5000" />
          </div>
          <div className="form-group">
            <label className="form-label">Cycle</label>
            <select className="form-input" name="cycle" value={form.cycle} onChange={handleChange}>
              {CYCLES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Number of Members</label>
            <input className="form-input" type="number" min="2" name="max_members" value={form.max_members} onChange={handleChange} placeholder="e.g. 5" />
          </div>
          <button className="btn btn-primary" style={{ width: '100%' }} onClick={goToStep2}>Continue →</button>
        </div>
      )}

      {step === 2 && (
        <div className="card">
          <p style={{ fontSize: '0.85rem', color: 'var(--text-2)', marginBottom: '1rem' }}>
            Invite members now by email, or skip this and share your invite code later. You (the organizer) already take slot #1.
          </p>
          {invites.map((email, idx) => (
            <div className="form-group" key={idx}>
              <label className="form-label">Member {idx + 2} Email</label>
              <input
                className="form-input" type="email" value={email}
                onChange={e => setInvites(invites.map((v, i) => i === idx ? e.target.value : v))}
                placeholder="member@example.com"
              />
            </div>
          ))}
          <button className="btn btn-ghost" style={{ marginBottom: '1.25rem' }} onClick={() => setInvites([...invites, ''])}>+ Add Another</button>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button className="btn btn-ghost" style={{ flex: 1 }} onClick={() => setStep(1)}>← Back</button>
            <button className="btn btn-primary" style={{ flex: 2 }} onClick={goToStep3}>Preview →</button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="card">
          {previewLoading ? (
            <p style={{ color: 'var(--text-2)' }}>Calculating rotation schedule…</p>
          ) : previewError ? (
            <div className="alert alert-error"><span>⚠</span><span>{previewError}</span></div>
          ) : preview && (
            <>
              <div className="grid-2" style={{ marginBottom: '1.25rem' }}>
                <div>
                  <div style={{ fontSize: '0.65rem', color: 'var(--text-3)', textTransform: 'uppercase' }}>Pot Per Cycle</div>
                  <div style={{ fontWeight: 700, color: '#00d9a5' }}>{formatCurrency(preview.expected_pot_amount)}</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.65rem', color: 'var(--text-3)', textTransform: 'uppercase' }}>Total Rounds Value</div>
                  <div style={{ fontWeight: 700 }}>{formatCurrency(preview.total_rounds_amount)}</div>
                </div>
              </div>
              <div className="table-wrapper" style={{ maxHeight: 260, overflowY: 'auto', marginBottom: '1.25rem' }}>
                <table>
                  <thead><tr><th>Cycle</th><th>Due Date</th><th>Payout</th></tr></thead>
                  <tbody>
                    {preview.schedule.map(row => (
                      <tr key={row.cycle_number}>
                        <td style={{ textAlign: 'center' }}>{row.cycle_number}</td>
                        <td>{row.due_date}</td>
                        <td>{formatCurrency(row.expected_pot_amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {error && <div className="alert alert-error" style={{ marginBottom: '1rem' }}><span>⚠</span><span>{error}</span></div>}
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button className="btn btn-ghost" style={{ flex: 1 }} onClick={() => setStep(2)}>← Back</button>
                <button className="btn btn-primary" style={{ flex: 2 }} disabled={loading} onClick={handleSubmit}>
                  {loading ? 'Creating…' : 'Create Group'}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
